/**
 * OIDC redirect landing (portal-spec §0). id.heivol.com sends the browser back
 * here with ?code&state. We verify state (CSRF), then hand the code to the API
 * BFF (POST /auth/token) which does the confidential-client exchange and returns
 * the id_token (+ a rotating refresh token). We store them as `hv-jwt` /
 * `hv-refresh` and drop back to the app root, where the session store will call
 * me() and route to enrol/unlock. The refresh token powers the silent on-401
 * refresh in lib/client.ts.
 */
import { useEffect, useState } from 'react';
import { Card, ErrorNote } from '../components/ui';

const API_BASE = import.meta.env['VITE_LOCKER_API'] as string | undefined;

export function AuthCallback() {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const expected = sessionStorage.getItem('oidc_state');

    if (code === null || state === null) {
      setError('Missing authorization response. Please try signing in again.');
      return;
    }
    if (expected === null || state !== expected) {
      setError('Login could not be verified (state mismatch). Please try signing in again.');
      return;
    }
    sessionStorage.removeItem('oidc_state');

    if (API_BASE === undefined || API_BASE.length === 0) {
      setError('This build is not configured for HeivolID login.');
      return;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/token`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ code, redirectUri }),
        });
        if (!res.ok) {
          throw new Error(`token exchange failed: ${res.status}`);
        }
        const json = (await res.json()) as { access_token?: unknown; refresh_token?: unknown };
        if (typeof json.access_token !== 'string' || json.access_token.length === 0) {
          throw new Error('no token returned');
        }
        localStorage.setItem('hv-jwt', json.access_token);
        if (typeof json.refresh_token === 'string' && json.refresh_token.length > 0) {
          localStorage.setItem('hv-refresh', json.refresh_token);
        } else {
          // No refresh token issued — make sure a stale one from a previous
          // login can't be replayed.
          localStorage.removeItem('hv-refresh');
        }
        window.location.replace('/');
      } catch {
        setError('Sign-in failed. Please try again.');
      }
    })();
  }, []);

  return (
    <div className="grid min-h-full place-items-center p-4">
      <Card className="w-full max-w-sm space-y-3">
        {error === null ? (
          <p className="text-sm text-text-secondary">Signing you in…</p>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-text">Sign-in problem</h1>
            <ErrorNote>{error}</ErrorNote>
            <a href="/" className="text-sm text-accent-text underline">
              Back to sign in
            </a>
          </>
        )}
      </Card>
    </div>
  );
}
